/**
 * Hook for managing subscription state and operations
 */

import { useState, useCallback, useEffect } from 'react';
import { subscriptionApi } from '@/api/subscriptions';
import type {
  Plan,
  UserSubscription,
  ValidateCouponResponse,
  CheckUsageLimitResponse,
} from '@/types/subscription';

interface UseSubscriptionState {
  plans: Plan[];
  currentSubscription: UserSubscription | null;
  usageLimit: CheckUsageLimitResponse | null;
  loading: boolean;
  error: string | null;
}

interface UseSubscriptionActions {
  loadPlans: () => Promise<void>;
  loadCurrentSubscription: () => Promise<void>;
  loadUsageLimit: () => Promise<void>;
  validateCoupon: (code: string, planCode?: string) => Promise<ValidateCouponResponse>;
  createSubscription: (planCode: string, couponCode?: string) => Promise<UserSubscription>;
  upgradePlan: (planCode: string, couponCode?: string) => Promise<UserSubscription>;
  downgradePlan: (planCode: string, couponCode?: string) => Promise<UserSubscription>;
  cancelSubscription: () => Promise<UserSubscription>;
  clearError: () => void;
}

export function useSubscription(): UseSubscriptionState & UseSubscriptionActions {
  const [state, setState] = useState<UseSubscriptionState>({
    plans: [],
    currentSubscription: null,
    usageLimit: null,
    loading: false,
    error: null,
  });

  const handleError = useCallback((error: unknown) => {
    const message = error instanceof Error ? error.message : 'An error occurred';
    setState((prev) => ({ ...prev, error: message, loading: false }));
  }, []);

  const clearError = useCallback(() => {
    setState((prev) => ({ ...prev, error: null }));
  }, []);

  const loadPlans = useCallback(async () => {
    setState((prev) => ({ ...prev, loading: true, error: null }));
    try {
      const plans = await subscriptionApi.getPlans();
      setState((prev) => ({ ...prev, plans, loading: false }));
    } catch (error) {
      handleError(error);
    }
  }, [handleError]);

  const loadCurrentSubscription = useCallback(async () => {
    setState((prev) => ({ ...prev, loading: true, error: null }));
    try {
      const subscription = await subscriptionApi.getCurrentSubscription();
      setState((prev) => ({ ...prev, currentSubscription: subscription, loading: false }));
    } catch (error) {
      // Subscription not found is not necessarily an error
      setState((prev) => ({ ...prev, currentSubscription: null, loading: false }));
    }
  }, []);

  const loadUsageLimit = useCallback(async () => {
    setState((prev) => ({ ...prev, loading: true, error: null }));
    try {
      const usageLimit = await subscriptionApi.checkUsageLimit();
      setState((prev) => ({ ...prev, usageLimit, loading: false }));
    } catch (error) {
      // Usage limit not found is not necessarily an error
      setState((prev) => ({ ...prev, usageLimit: null, loading: false }));
    }
  }, []);

  const validateCoupon = useCallback(
    async (code: string, planCode?: string): Promise<ValidateCouponResponse> => {
      setState((prev) => ({ ...prev, loading: true, error: null }));
      try {
        const result = await subscriptionApi.validateCoupon({
          code,
          plan_code: planCode,
        });
        setState((prev) => ({ ...prev, loading: false }));
        return result;
      } catch (error) {
        handleError(error);
        throw error;
      }
    },
    [handleError]
  );

  const createSubscription = useCallback(
    async (planCode: string, couponCode?: string): Promise<UserSubscription> => {
      setState((prev) => ({ ...prev, loading: true, error: null }));
      try {
        const subscription = await subscriptionApi.createSubscription({
          plan_code: planCode,
          coupon_code: couponCode,
        });
        setState((prev) => ({
          ...prev,
          currentSubscription: subscription,
          loading: false,
        }));
        return subscription;
      } catch (error) {
        handleError(error);
        throw error;
      }
    },
    [handleError]
  );

  const upgradePlan = useCallback(
    async (planCode: string, couponCode?: string): Promise<UserSubscription> => {
      setState((prev) => ({ ...prev, loading: true, error: null }));
      try {
        const subscription = await subscriptionApi.upgradePlan(planCode, couponCode);
        setState((prev) => ({
          ...prev,
          currentSubscription: subscription,
          loading: false,
        }));
        return subscription;
      } catch (error) {
        handleError(error);
        throw error;
      }
    },
    [handleError]
  );

  const downgradePlan = useCallback(
    async (planCode: string, couponCode?: string): Promise<UserSubscription> => {
      setState((prev) => ({ ...prev, loading: true, error: null }));
      try {
        const subscription = await subscriptionApi.downgradePlan(planCode, couponCode);
        setState((prev) => ({
          ...prev,
          currentSubscription: subscription,
          loading: false,
        }));
        return subscription;
      } catch (error) {
        handleError(error);
        throw error;
      }
    },
    [handleError]
  );

  const cancelSubscription = useCallback(async (): Promise<UserSubscription> => {
    setState((prev) => ({ ...prev, loading: true, error: null }));
    try {
      const subscription = await subscriptionApi.cancelSubscription();
      setState((prev) => ({
        ...prev,
        currentSubscription: subscription,
        loading: false,
      }));
      return subscription;
    } catch (error) {
      handleError(error);
      throw error;
    }
  }, [handleError]);

  return {
    // State
    ...state,
    // Actions
    loadPlans,
    loadCurrentSubscription,
    loadUsageLimit,
    validateCoupon,
    createSubscription,
    upgradePlan,
    downgradePlan,
    cancelSubscription,
    clearError,
  };
}
